import React from 'react';
import { BigNumber } from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';

import { PurchaseLine, ReceiptStatus } from 'modules/purchase/models';
import { ApplicationState, getInvoiceLines, isQuantityBasedLine } from 'modules/app/store';
import { I18nFormatter } from 'modules/core/class';

import styles from '../PurchaseLineWrapper/Wrapper.module.scss';
import { Box, SpaceBetween } from '@amzn/awsui-components-react';

interface PurchaseLineHeaderProps {
    line: PurchaseLine;
}

export const PurchaseLineHeader = (props: PurchaseLineHeaderProps) => {
    const { t } = useTranslation('purchase');

    const { line } = props;

    const invoiceLines = useSelector(
        (state: ApplicationState) => getInvoiceLines(state, line.lineNumber),
        shallowEqual
    );

    const isQuantityBased = isQuantityBasedLine(line);

    const QuantityLineAttributes = () => {
        return (
            <>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.price')}</Box>
                    <div>{I18nFormatter.quantity(t, line?.pricePerUnit)}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.totalAmount')}</Box>
                    <div>{I18nFormatter.amount(t, line?.amount)}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.totalQuantity')}</Box>
                    <div>{I18nFormatter.quantity(t, line?.quantity)}</div>
                </Box>
            </>
        );
    };

    const receivedAmount = () =>
        line?.receipts
            .filter(r => r.receiptStatus === ReceiptStatus.POSTED || r.receiptStatus === ReceiptStatus.PENDING_VOID)
            .map(r => new BigNumber(r.receiptAmount))
            .reduce((prev, curr) => prev.plus(curr), new BigNumber(0))
            .toNumber();

    const invoiceAmount = () =>
        invoiceLines
            .map(i => new BigNumber(i.amount))
            .reduce((prev, curr) => prev.plus(curr), new BigNumber(0))
            .toNumber();

    return (
        <SpaceBetween size="l">
            <div className={styles['grid-container']}>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.item')} </Box>
                    <div>{line.description}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.accountingString')}</Box>
                    <div>{line.account?.accountingString}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">Account Description</Box>
                    <div>{line.account?.name}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">Need By Date</Box>
                    <div>{I18nFormatter.date(t, line?.needByDate)}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.currencyCode')}</Box>
                    <div>{line?.currencyCode}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.invoicedAmount')}</Box>
                    <div>{I18nFormatter.amount(t, invoiceAmount())}</div>
                </Box>
                <Box padding="l" text-align="left">
                    <Box variant="awsui-key-label">{t('line.receivedAmount')}</Box>
                    <div>{I18nFormatter.amount(t, receivedAmount())}</div>
                </Box>
                {isQuantityBased && <QuantityLineAttributes />}
            </div>
        </SpaceBetween>
    );
};
